import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { Session } from '@supabase/supabase-js';
import { getOtp } from '../api';
import { isAllowedOrigin, topOrigin } from '../redirects';
import { supabase } from '../supabase';
import { useSession } from '../useSession';

// /RefreshToken: framed by the apps' "session expired" dialog. Posts {name: "otpReady", otp} to the top window.
//
// No session required: Microsoft's sign-in page refuses to be framed, so without a session it asks the user to sign
// in on a new tab. That tab ends on /RefreshTokenSuccess, which posts {name: "RefreshTokenSuccess"} back here
// (window.opener, so the new tab must not be opened with noopener).
export const RefreshToken: React.FC = () => {
    const current = useSession();
    // Session the other tab signed in (getSession() re-reads the cookies but raises no auth event).
    const [signedIn, setSignedIn] = useState<Session | null>(null);
    const session = signedIn ?? current;
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        function onMessage (e: MessageEvent) {
            if (e.origin !== window.location.origin || e.data?.name !== 'RefreshTokenSuccess') {
                return;
            }
            supabase.auth.getSession()
                .then(({ data }) => setSignedIn(data.session))
                .catch((err: Error) => setError(err.message));
        }
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        if (!session || otpSent) return;
        getOtp()
            .then(otp => {
                if (window.self !== window.top) {
                    const origin = topOrigin(window.location, document.referrer);
                    if (!isAllowedOrigin(origin)) {
                        setError(`Not sending a login to ${origin ?? 'an unknown site'}.`);
                        return;
                    }
                    window.top!.postMessage({ name: 'otpReady', otp }, origin);
                }
                setOtpSent(true);
            })
            .catch((e: Error) => setError(e.message));
    }, [session, otpSent]);

    if (error) {
        return <Box className="center"><Typography color="error">{error}</Typography></Box>;
    }
    if (session === undefined) {
        return null;
    }
    if (!session) {
        return (
            <Box className="center" sx={{ gap: 2 }}>
                <Typography variant="h6">Session not found.</Typography>
                <Button variant="contained" onClick={() => window.open('/auth/?redirectToPath=/RefreshTokenSuccess?closeTab=true', '_blank')}>
                    Please click here to login in a new tab
                </Button>
            </Box>
        );
    }
    return <Box className="center"><Typography>{otpSent ? 'You are logged in successfully.' : 'Loading ...'}</Typography></Box>;
};
